import React, { useState, useEffect, useRef } from 'react';
import {
  Bell,
  User,
  Home,
  BarChart4,
  ShoppingBag,
  Store,
  Building2,
  FileText,
  Users,
  CreditCard,
  Settings,
  LogOut,
  ChevronDown,
  UserCircle,
  UserRound,
  CheckCircle,
  Activity,
  Calculator,
  Layers
} from 'lucide-react';
import { useAppSelector, useAppDispatch } from '../../store/hooks/reduxHooks';
import { UserRole } from '../../types/auth';
import { useLocation, useNavigate } from 'react-router-dom';
import { handleLogout } from '../../utils/sessionManager';

export const Header = () => {
  const { user } = useAppSelector(state => state.auth);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  // const [notifications, setNotifications] = useState(3);
  // const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const location = useLocation();
  const profileDropdownRef = useRef<HTMLDivElement>(null);

  // Update the current time every minute
  useEffect(() => {
    const timerId = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timerId);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target as Node)) {
        setShowProfileDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Navigation to profile page
  const goToProfile = () => {
    // Get the base path based on user role
    const basePath = user?.role === UserRole.SUPERADMIN
      ? '/superadmin'
      : user?.role === UserRole.ADMIN
        ? '/admin'
        : '/cleverpeuser';

    navigate(`${basePath}/profile`);
    setShowProfileDropdown(false);
  };

  // Handle logout
  const onLogout = () => {
    handleLogout(dispatch);
  };

  // Get page icon and title based on the current location
  const getPageInfo = () => {
    const path = location.pathname;
    const parts = path.split('/').filter(part => part !== '');

    if (parts.length === 0 || parts[parts.length - 1] === 'dashboard') {
      return {
        title: 'Dashboard',
        description: 'Complete Dashboard Overview at a Glance',
        icon: <Home className="mr-2 text-brand-blue" size={28} />
      };
    }

    const lastPart = parts[parts.length - 1];

    // Map of route segments to page info
    const pageInfoMap: Record<string, { title: string; description: string; icon: React.ReactNode }> = {
      'dashboard': {
        title: 'Dashboard',
        description: 'Complete Dashboard Overview at a Glance',
        icon: <Home className="mr-2 text-brand-blue" size={28} />
      },
      'manage-admins': {
        title: 'Manage Admins',
        description: 'Administer user accounts and permissions',
        icon: <Users className="mr-2 text-brand-blue" size={28} />
      },
      'manage-partner': {
        title: 'Manage Partners',
        description: 'View and manage Partners/Brands accounts',
        icon: <Building2 className="mr-2 text-brand-blue" size={28} />
      },

      'manage-merchant-kyc-pending': {
        title: 'Manage KYC Pending Merchants',
        description: 'View and manage KYC Pending Merchant accounts',
        icon: <Store className="mr-2 text-brand-blue" size={28} />
      },
      'manage-product': {
        title: 'Manage Products',
        description: 'Catalog and inventory management',
        icon: <ShoppingBag className="mr-2 text-brand-blue" size={28} />
      },
      'manage-order': {
        title: 'Manage Orders',
        description: 'Track and process customer orders',
        icon: <FileText className="mr-2 text-brand-blue" size={28} />
      },
      'profile': {
        title: 'Profile',
        description: 'View your profile details',
        icon: <UserCircle className="mr-2 text-brand-blue" size={28} />
      },
      'reports-pre-approvals': {
        title: 'Pre-Approval Reports',
        description: 'Download and analyze pre-approval data',
        icon: <BarChart4 className="mr-2 text-brand-blue" size={28} />
      },
      'reports-settlements': {
        title: 'Settlement Reports',
        description: 'View and export payment settlement data',
        icon: <CreditCard className="mr-2 text-brand-blue" size={28} />
      },
      'reports-merchant-data': {
        title: 'Merchant Data Reports',
        description: 'Analytics and insights on merchant performance',
        icon: <BarChart4 className="mr-2 text-brand-blue" size={28} />
      },
      'manage-customers': {
        title: 'Manage Customers',
        description: 'View and manage customer accounts',
        icon: <UserRound className="mr-2 text-brand-blue" size={28} />
      },
      'pre-approval-logs': {
        title: 'Pre Approval Logs',
        description: 'View and manage pre-approval logs',
        icon: <CheckCircle className="mr-2 text-brand-blue" size={28} />
      },
      'manage-scheme': {
        title: 'Manage Schemes',
        description: 'Create and modify financing schemes',
        icon: <Settings className="mr-2 text-brand-blue" size={28} />
      },
      'manage-scheme-bucket': {
        title: 'Manage Scheme Buckets',
        description: 'Create and organize scheme buckets',
        icon: <Layers className="mr-2 text-brand-blue" size={28} />
      },
      'external-logs': {
        title: 'External API Logs',
        description: 'Monitor all external API requests and responses',
        icon: <Activity className="mr-2 text-brand-blue" size={28} />
      },
      'calculator': {
        title: 'EMI Calculator',
        description: 'View & Compare Schemes',
        icon: <Calculator className="mr-2 text-brand-blue" size={28} />
      }
    };

    // Return page info if found in map, otherwise generate from path
    if (pageInfoMap[lastPart]) {
      return pageInfoMap[lastPart];
    }

    // Default behavior for unknown pages
    const title = lastPart.charAt(0).toUpperCase() + lastPart.slice(1).replace(/-/g, ' ');
    return {
      title,
      description: `Manage your ${title.toLowerCase()}`,
      icon: <FileText className="mr-2 text-brand-blue" size={28} />
    };
  };

  // Get greeting based on time of day
  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  };

  // Get role color based on user role
  const getRoleColor = () => {
    if (!user) return 'bg-gray-500';

    switch (user.role) {
      case UserRole.SUPERADMIN:
        return 'bg-purple-600';
      case UserRole.ADMIN:
        return 'bg-blue-600';
      case UserRole.CLEVERPE_USER:
        return 'bg-green-600';
      default:
        return 'bg-gray-500';
    }
  };

  // Format role name for display
  const formatRole = (role: string) => {
    return role.replace(/_/g, ' ').split(' ').map(
      word => word.charAt(0) + word.slice(1).toLowerCase()
    ).join(' ');
  };

  if (!user) return null;

  const pageInfo = getPageInfo();

  return (
    <header className="bg-white border-b border-gray-200">
      <div className="flex items-center justify-between px-4 py-3 lg:px-6">
        {/* Left side - Page info */}
        <div>
          <h1 className="flex items-center text-2xl text-gray-800 font-gilroy-bold">
            {pageInfo.icon}
            {pageInfo.title}
          </h1>
          <p className="text-sm text-brand-gray">
            {pageInfo.description}
          </p>
        </div>

        {/* Right side - actions */}
        <div className="flex items-center gap-3">
          {/* Date and Time */}
          <div className="hidden text-right md:block">
            <p className="text-sm font-gilroy-medium text-brand-dark">
              {currentTime.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
            <p className="text-xs text-brand-gray">
              {currentTime.toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'short',
                day: 'numeric'
              })}
            </p>
          </div>

          {/* Notifications */}
          {/* <div className="relative">
            <button
              className="relative p-2 transition rounded-full hover:bg-gray-100"
              onClick={() => setShowNotifications(!showNotifications)}
            >
              <Bell size={20} className="text-gray-600" />
              {notifications > 0 && (
                <span className="absolute top-0 right-0 flex items-center justify-center w-4 h-4 text-xs text-white bg-red-500 rounded-full">
                  {notifications}
                </span>
              )}
            </button>
          </div> */}

          {/* User profile with dropdown */}
          <div className="relative" ref={profileDropdownRef}>
            <button
              className="flex items-center gap-2 p-1 transition-colors duration-200 rounded-lg hover:bg-gray-50"
              onClick={() => setShowProfileDropdown(!showProfileDropdown)}
            >
              <div className="flex flex-col items-end hidden mr-1 sm:flex">
                <span className="text-sm font-gilroy-medium text-brand-dark">
                  {getGreeting()}, {user.name || 'User'}
                </span>
                <div className="flex items-center gap-1">
                  <span className={`inline-block w-2 h-2 rounded-full ${getRoleColor()}`}></span>
                  <span className="text-xs text-brand-gray">
                    {formatRole(user.role)}
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-center w-10 h-10 text-white rounded-full bg-gradient-to-r from-gradient-blue-1 to-gradient-blue-2 font-gilroy-semibold">
                {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
              </div>
              <ChevronDown
                size={16}
                className={`text-gray-500 transition-transform duration-200 ${showProfileDropdown ? 'rotate-180' : ''}`}
              />
            </button>

            {/* Profile dropdown */}
            {showProfileDropdown && (
              <div className="absolute right-0 z-50 w-48 py-1 mt-2 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 animate-fadeIn">
                <div className="px-4 py-2 border-b border-gray-100">
                  <p className="text-sm truncate font-gilroy-semibold text-brand-dark">
                    {user.name}
                  </p>
                  <p className="text-xs truncate text-brand-gray">
                    {user.email}
                  </p>
                </div>
                <button
                  onClick={goToProfile}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-100"
                >
                  <User size={16} className="mr-2" />
                  My Profile
                </button>
                <button
                  onClick={onLogout}
                  className="flex items-center w-full px-4 py-2 text-sm text-red-600 transition-colors hover:bg-gray-100"
                >
                  <LogOut size={16} className="mr-2" />
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}; 